import { api } from "./api";
import { useStore } from "./store";

/**
 * Probeer een bericht in een conversatie zichtbaar te scrollen. Als 't
 * niet in de DOM staat, laden we tot 10 oudere pagina's (≈ 500 berichten)
 * totdat 't bericht in de stream zit, en pulsen we daarna een korte
 * ring-highlight zodat de gebruiker ziet waar 'ie geland is.
 *
 * Wordt gebruikt door PinnedStrip, MentionsDrawer, FileBrowser en
 * MessageStream's quote-reply-klik. Voorheen was deze logica vier keer
 * gedupliceerd; nu één plek met identiek gedrag.
 *
 * `switchActive` doet eerst setActiveConversation; zet 'm op false als
 * de caller al in de juiste conversatie staat (zoals in MessageStream).
 */
export async function scrollToMessage(opts: {
  conversationId: number;
  messageId: number;
  switchActive?: boolean;
  /** Max pagina's terug die we laden voordat we opgeven. Default 10. */
  maxPages?: number;
}): Promise<void> {
  const { conversationId, messageId } = opts;
  const max = opts.maxPages ?? 10;

  if (opts.switchActive) {
    useStore.getState().setActiveConversation(conversationId);
    // Wacht tot MessageStream voor de nieuwe conversatie gemount is.
    // Hard-coded 80ms haalde 't op trage Android-toestellen niet altijd;
    // polling tot max 800ms (10 × 80ms) blijft snel voelen op snelle
    // toestellen en geeft langzame de ruimte om te renderen.
    for (let i = 0; i < 10; i++) {
      await new Promise((r) => setTimeout(r, 80));
      if (document.querySelector(`[data-message-id="${messageId}"]`)) break;
      // Of de stream is gemount maar 't bericht ligt buiten de huidige
      // pagina — dat detecteren we via messagesByConv. Beide signalen
      // betekenen "klaar om verder te gaan".
      if ((useStore.getState().messagesByConv[conversationId] ?? []).length > 0) break;
    }
  }

  let el = document.querySelector(`[data-message-id="${messageId}"]`);
  if (!el) {
    for (let i = 0; i < max; i++) {
      const state = useStore.getState();
      const list = state.messagesByConv[conversationId] ?? [];
      if (list.some((m) => m.id === messageId)) break;
      if (!state.hasMoreByConv[conversationId]) break;
      const oldest = list[0];
      if (!oldest) break;
      const { messages: older } = await api.listMessages(conversationId, oldest.id, 50);
      state.prependMessages(conversationId, older, older.length === 50);
      if (older.length === 0) break;
    }
    // React rendert nieuwe rijen in 'n volgend frame.
    await new Promise((r) => requestAnimationFrame(() => r(undefined)));
    el = document.querySelector(`[data-message-id="${messageId}"]`);
  }
  if (!el) return;

  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("ring-2", "ring-accent");
  setTimeout(() => {
    el!.classList.remove("ring-2", "ring-accent");
  }, 1400);
}
